/**
 * What the page says of credits (docs/SPEC.md §15.1; P28-T08). The owner's
 * rule (2026-10-21): "only estimates before the expensive ones" — an estimate
 * and a confirm before every GPU render and before a model action of
 * `PRICE_CONFIRM_CREDITS` or more; the block with **Top up** whenever the
 * balance is below an estimate; no cost line under an answer; the ad's total
 * in its details and its Media folder.
 */

/** `{key}` filled from `params`; numbers with thousands separators. */
export function sayCredits(
  sentence: string,
  params: Record<string, string | number> = {},
): string {
  return sentence.replace(/\{(\w+)\}/g, (whole, key: string) => {
    if (!Object.prototype.hasOwnProperty.call(params, key)) return whole;
    const value = params[key];
    return typeof value === "number"
      ? value.toLocaleString("en-US")
      : String(value);
  });
}

export const CREDITS = {
  /** The confirm before an expensive action: what it is, its estimate. */
  confirm: "{what} — about {credits} credits. Go ahead?",
  goAhead: "Go ahead",
  notNow: "Not now",
  /** The block: the balance is below the estimate (402 `insufficient_credits`). */
  blocked: "This needs about {needed} credits; you have {available}.",
  /** The estimate is not known (the route said only that it was refused). */
  blockedUnknown: "You do not have enough credits for this.",
  topUp: "Top up",
  /** What each confirm is about, by where it is asked. */
  what: {
    start: "Starting the brief",
    answer: "The brief's next step",
    change: "This change",
    retry: "Writing the film again",
    rt: "The real-time film",
    final: "The ray-traced final",
    still: "A true render of this moment",
    photo: "Writing the scenes again from your photo",
  },
  /** The ad's total spend (the ad's details, its Media folder). */
  adTotal: "This ad so far: {credits} credits",
  adTotalNone: "This ad so far: nothing charged",
  folderTotal: "{credits} credits so far",
  details: "Details",
  created: "Made {date}",
  /** The composer's line under Start: the real rule (§15.1). */
  composer:
    "The conversation and the film are charged by what they use; you are asked first before anything costly starts.",
} as const;

export type ConfirmWhat = keyof typeof CREDITS.what;

/** "This change — about 640 credits. Go ahead?" */
export function confirmSentence(what: ConfirmWhat, credits: number): string {
  return sayCredits(CREDITS.confirm, { what: CREDITS.what[what], credits });
}

/** "This needs about 1,350 credits; you have 100." */
export function blockedSentence(needed: number, available: number): string {
  return sayCredits(CREDITS.blocked, { needed, available });
}
